import { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { channelToIconMapping } from 'components/domain/lead/LeadChannelIcon';
import {
  getColorByAffiliate,
  getLeadCheckInUtcDate,
  getLeadCheckOutUtcDate,
} from 'utils/lead/leadUtils';
import useOpenLeadModal from 'components/domain/lead/modal/useOpenLeadModal';
import Tooltip from 'components/tooltip/Tooltip';
import { LeadStatus, LeadType, LeadColor } from 'models/Leads';
import { LeadListItemProps } from 'pages/dashboard/DashboardLead.types';
import LeadListItemBasicInfo from 'components/domain/lead/list/LeadListItemBasicInfo';
import LeadListItemDuplicateTag from 'components/domain/lead/list/LeadListItemDuplicateTag';
import LeadListItemPriceTag from 'components/domain/lead/list/LeadListItemPriceTag';
import LeadListItemViewButton from 'components/domain/lead/list/LeadListItemViewButton';
import LeadListItemRiskScoreTag from 'components/domain/lead/list/LeadListItemRiskScoreTag';
import { LeadListTag } from 'components/domain/lead/list/LeadListTag.styles';
import theme from 'styles/theme';
import LeadMultiReservationLabel from 'components/domain/lead/LeadMultiReservationLabel';
import useScreenBreakpoints from 'hooks/useScreenBreakpoints';
import LeadListItemAlterationRequestedTag from 'components/domain/lead/list/LeadListItemAlterationRequestedTag';
import LeadListItemReviewAlterationButton from 'components/domain/lead/list/LeadListItemReviewAlterationButton';
import {
  ActionsWrapper,
  AnchorTag,
  ButtonTag,
  ChannelWrapper,
  Container,
  Content,
  LeadHeader,
  LeftSide,
  RightSide,
  StatusIconWrapper,
  TagsWrapper,
  TopSide,
} from './LeadListItem.styles';
import LeadStatusIcon from './leadIcons/LeadStatusIcon';

const LeadListItem = ({ leadData }: LeadListItemProps) => {
  const {
    uid,
    status,
    channel,
    createdDateTimeString,
    onSign,
    onIgnore,
    assignee,
    leadType,
    paidInFull,
    referrer,
    disableActions,
    agreementSigned,
    hasCompletedPreArrivalForm,
    isLowOpacity,
    showStatusRing,
    showGuestCounter,
    showBookAndInquiryHeader,
    showCheckinInHeader,
    showCheckoutInHeader,
    showLeadStatusTags,
    showLeadStatusIcons,
    showAssigneePhoto,
    showProcessActions,
    showAssignToMe,
    showMessageAction,
  } = leadData;

  const { t } = useTranslation();
  const { updateLeadModal } = useOpenLeadModal();
  const { isXS } = useScreenBreakpoints();

  const checkInDate = getLeadCheckInUtcDate(leadData);
  const checkOutDate = getLeadCheckOutUtcDate(leadData);
  // @ts-expect-error TS2769 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
  const createdDate = new Date(createdDateTimeString);

  const isInquiry = leadType === LeadType.INQUIRY;

  const channelColor = getColorByAffiliate(leadData.affiliate);
  const referrerColor = LeadColor.DEFAULT;

  const leadHeader = {
    BOOKING: (
      <>
        {t('componentDashboardLead.bookingTypeHeader', {
          date: createdDate,
        })}{' '}
        {referrer && <span>{t('componentDashboardLead.on')} </span>}
        {referrer && <span style={{ color: referrerColor }}>{referrer} </span>}
        {t('componentDashboardLead.via')}
      </>
    ),

    INQUIRY: (
      <>
        {t('componentDashboardLead.inquiryTypeHeader', {
          date: createdDate,
        })}{' '}
        {referrer && <span>{t('componentDashboardLead.on')} </span>}
        {referrer && <span style={{ color: referrerColor }}>{referrer} </span>}
        {t('componentDashboardLead.via')}
      </>
    ),
  };

  const pendingLeadHeader = {
    BOOKING: (
      <>
        {t('componentDashboardLead.pendingBookingTypeHeader', {
          date: createdDate,
        })}{' '}
        {referrer && <span>{t('componentDashboardLead.on')} </span>}
        {referrer && <span style={{ color: referrerColor }}>{referrer} </span>}
        {t('componentDashboardLead.via')}
      </>
    ),
  };

  const viewLeadClick = (event: MouseEvent<HTMLDivElement>) => {
    if ((event.target as Element).closest('.lead-view-button')) {
      return;
    }

    updateLeadModal({ leadUid: uid });
  };

  const isStatusPending = status === LeadStatus.PENDING;

  const isIgnoreActionEnabled =
    isInquiry &&
    showProcessActions &&
    (status === LeadStatus.NEW || status === LeadStatus.QUOTE_SENT);

  return (
    <Container
      data-testid={`dashboard-lead-uid-${uid}`}
      // @ts-expect-error TS2769 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      isLowOpacity={isLowOpacity}
    >
      <TopSide>
        <LeadHeader>
          {showBookAndInquiryHeader && (
            <p data-testid="dashboard-lead-header-new-mode">
              {isStatusPending ? (
                <span>{pendingLeadHeader.BOOKING}</span>
              ) : (
                // @ts-expect-error TS2538 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
                <span>{leadHeader?.[leadType]}</span>
              )}{' '}
              <ChannelWrapper channelColor={channelColor}>
                {/*
                 // @ts-expect-error TS2538 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified */}
                {channelToIconMapping[channel?.channel]}
                {!isXS && channel?.bestProfile}
              </ChannelWrapper>
              <LeadMultiReservationLabel lead={leadData} />
            </p>
          )}

          {showCheckinInHeader && (
            <p
              data-testid="dashboard-lead-header-checkin-mode"
              className="checkin-header"
            >
              <ChannelWrapper channelColor={channelColor}>
                {/*
                 // @ts-expect-error TS2538 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified */}
                {channelToIconMapping[channel?.channel]}
              </ChannelWrapper>
              {t('componentDashboardLead.checkinTypeHeader', {
                date: checkInDate,
              })}
              <LeadMultiReservationLabel lead={leadData} />
            </p>
          )}

          {showCheckoutInHeader && (
            <p
              data-testid="dashboard-lead-header-checkout-mode"
              className="checkin-header"
            >
              <ChannelWrapper channelColor={channelColor}>
                {/*
                 // @ts-expect-error TS2538 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified */}
                {channelToIconMapping[channel?.channel]}
              </ChannelWrapper>
              {t('componentDashboardLead.checkoutTypeHeader', {
                date: checkOutDate,
              })}
              <LeadMultiReservationLabel lead={leadData} />
            </p>
          )}
        </LeadHeader>

        <div>
          {showLeadStatusTags && (
            <TagsWrapper data-testid="dashboard-lead-tags-container">
              {isStatusPending && (
                <LeadListTag backgroundColor="#FF7254">
                  {t('componentDashboardLead.pending')}
                </LeadListTag>
              )}
              <LeadListItemDuplicateTag lead={leadData} />
              <LeadListItemAlterationRequestedTag lead={leadData} />
              <LeadListItemPriceTag lead={leadData} />
            </TagsWrapper>
          )}

          {showLeadStatusIcons && (
            <StatusIconWrapper data-testid="dashboard-lead-status-icons-container">
              <Tooltip
                id="lead-tick-icon-tooltip"
                text={t('componentDashboardLead.preArrivalIconMessage')}
              >
                <LeadStatusIcon
                  id="lead-tick-icon"
                  statusColor={
                    hasCompletedPreArrivalForm ? 'success' : 'neutral'
                  }
                  type="tick"
                />
              </Tooltip>
              <Tooltip
                id="lead-rental-icon-tooltip"
                text={t('componentDashboardLead.renatlIconMessage')}
              >
                <LeadStatusIcon
                  id="lead-rental-icon"
                  statusColor={agreementSigned ? 'success' : 'neutral'}
                  type="rental"
                />
              </Tooltip>
              <Tooltip
                id="lead-dollar-icon-tooltip"
                text={t('componentDashboardLead.paymentIconMessage')}
              >
                <LeadStatusIcon
                  id="lead-dollar-icon"
                  statusColor={paidInFull ? 'success' : 'warning'}
                  type="dollar"
                />
              </Tooltip>
            </StatusIconWrapper>
          )}
        </div>
      </TopSide>
      <Content>
        <LeftSide onClick={viewLeadClick}>
          <LeadListItemBasicInfo
            lead={leadData}
            showGuestCounter={showGuestCounter}
            showStatusRing={showStatusRing}
          />
        </LeftSide>

        <RightSide>
          <ActionsWrapper data-testid="dashboard-lead-action-container">
            <div className="dashboard-lead-action-container-img-wrapper show-not-on-hover">
              {!!assignee?.uid && showAssigneePhoto && assignee?.picture && (
                <img
                  data-testid="dashboard-lead-assignee-picture"
                  src={assignee?.picture}
                  alt={assignee?.bestProfile}
                />
              )}
              {!showLeadStatusIcons && (
                <LeadListItemRiskScoreTag lead={leadData} />
              )}
            </div>

            <div className="show-on-hover">
              {!assignee?.uid && showAssignToMe && (
                <ButtonTag
                  data-testid="dashboard-lead-assignee-button"
                  // @ts-expect-error TS2722 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
                  onClick={() => onSign(uid)}
                  backgroundColor="#8a5bda"
                  disabled={disableActions}
                >
                  {t('componentDashboardLead.assignToMe')}
                </ButtonTag>
              )}

              <LeadListItemViewButton
                backgroundColor={
                  showLeadStatusTags ? undefined : theme.colors.bgGreen
                }
                lead={leadData}
              />

              <LeadListItemReviewAlterationButton lead={leadData} />

              {isIgnoreActionEnabled && (
                <ButtonTag
                  data-testid="dashboard-lead-ignore-button"
                  // @ts-expect-error TS2722 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
                  onClick={() => onIgnore(uid)}
                  backgroundColor="#6c757d"
                  disabled={disableActions}
                >
                  {t('componentDashboardLead.ignore')}
                </ButtonTag>
              )}

              {showMessageAction && (
                <AnchorTag
                  data-testid="dashboard-lead-message-button"
                  href={`#/inbox?leadUid=${uid}`}
                  target="_blank"
                  rel="noreferrer"
                  backgroundColor="#6d747d"
                >
                  {t('componentDashboardLead.message')}
                </AnchorTag>
              )}
            </div>
          </ActionsWrapper>
        </RightSide>
      </Content>
    </Container>
  );
};

export default LeadListItem;
